import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import {FC, useState, useEffect} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Card, Row, Col, Button} from 'react-bootstrap'
import {useParams, useSearchParams} from 'react-router-dom'
import {ListComodity} from './components/ListComodity'
import {
  fetchDataTradeFlow,
  getDetailCountry,
  getDetailTradeFlow,
  importDataManual,
} from './core/_requests'

import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {CountryType, ListComodityResultRowType} from './core/_models'
import ModalImportData from './components/ModalImportData'
import './TradeDataManagementDetailPage.scss'
import {getNameByISO3} from '../../../libs/misc'

const ReactSwal = withReactContent(Swal)

const TradeDataManagementDetailPage: FC<{subject: string}> = ({subject}) => {
  const axiosPrivate = useAxiosPrivate()
  const [searchParams, setSearchParams] = useSearchParams()

  const [isSyncing, setIsSyncing] = useState(false)
  const [syncProgress, setSyncProgress] = useState(0)
  const [syncMessage, setSyncMessage] = useState('Synchronizing data')

  const [showResultSection, setShowResultSection] = useState(false)
  const [showNoResultSection, setShowNoResultSection] = useState(false)
  const [availabilityComtrade, setAvailabilityComtrade] = useState(false)

  const [showImportModal, setShowImportModal] = useState(false)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importLoading, setImportLoading] = useState(false)
  const [importError, setImportError] = useState<string | null>(null)

  const {countrycode} = useParams()
  const [year, setYear] = useState<string>(searchParams.get('year') || '2023')
  const [hsCode, setHsCode] = useState<string>(searchParams.get('hs_vr') || 'H4')
  const [sourceType, setSourceType] = useState<string>(
    searchParams.get('source_type') || 'comtrade'
  )

  const [detailCountry, setDetailCountry] = useState<CountryType>({
    rep: '',
    name: '',
    repcode: '',
    record_size: 0,
  })

  const [listComodityTableData, setListComodityTableData] =
    useState<Array<ListComodityResultRowType> | null>()

  const [countData, setCountData] = useState(0)
  const [isLoadingComodity, setIsLoadingComodity] = useState(true)

  useEffect(() => {
    setIsLoadingComodity(true)
    const runEffect = async () => {
      if (!countrycode) return

      setDetailCountry((prev) => ({
        ...prev,
        rep: countrycode,
        name: getNameByISO3(countrycode),
      }))

      // 1. Ensure detailCountry is fetched first
      try {
        const res = await getDetailCountry(axiosPrivate, {rep: countrycode})
        const resData = res.data.data

        setShowResultSection(true)
        if (resData.record_size === 0) {
          setDetailCountry((prev) => ({
            ...prev,
            record_size: resData.record_size,
          }))

          setShowNoResultSection(true)
          setAvailabilityComtrade(resData.file_url)
        }
      } catch (err) {
        console.error('Failed to load country details:', err)
        return
      }

      const tradeFlowPromise = getDetailTradeFlow(axiosPrivate, {
        code: hsCode,
        rep: countrycode,
        year,
        source_type: sourceType,
      })

      try {
        const resDetail = await tradeFlowPromise

        if (resDetail.data.table_data.length > 0) {
          setListComodityTableData(resDetail.data.table_data)
          setCountData(resDetail.data.count)
        } else {
          setListComodityTableData([])
        }
      } catch (err: any) {
        if (err.response?.status !== 404) {
          ReactSwal.fire({
            title: 'Error!',
            html: `${err?.data?.message || 'An unexpected error occurred'}`,
            icon: 'error',
            showCloseButton: true,
            showCancelButton: true,
            showConfirmButton: false,
            cancelButtonText: 'Close',
            customClass: {
              title: 'fw-bold',
              cancelButton: 'btn btn-danger btn-sm',
            },
            padding: '1rem',
          })
        }
      } finally {
        setIsLoadingComodity(false)
      }
    }

    runEffect()
  }, [])

  useEffect(() => {
    let interval
    if (isSyncing) {
      interval = setInterval(() => {
        setSyncMessage((prev) => {
          if (prev.endsWith('...')) return 'Synchronizing data'
          return prev + '.'
        })
      }, 500)
    }

    return () => clearInterval(interval)
  }, [isSyncing])

  // handle apply filter
  // const handleApplyFilter = (filters: {year: string; hsCode: string; sourceType: string}) => {
  //   setYear(filters.year)
  //   setHsCode(filters.hsCode)
  //   setSourceType(filters.sourceType)

  //   setSearchParams({
  //     year: filters.year,
  //     hs_vr: filters.hsCode,
  //     source_type: filters.sourceType,
  //   })
  // }

  // handle sync
  const handleSync = () => {
    setIsSyncing(true)
    setSyncProgress(0)

    fetchDataTradeFlow(axiosPrivate, {
      rep: detailCountry.rep,
      year: year,
      hs_vr: hsCode,
    })
      .then((res) => {
        if (res.status === 200) {
          ReactSwal.fire({
            title: 'Synchronization Complete',
            icon: 'success',
            confirmButtonText: 'OK',
          }).then((result) => {
            if (result.isConfirmed) {
              window.location.reload()
            }
          })
        }
      })
      .catch((e) => {
        const resError = e.response
        ReactSwal.fire({
          title: 'Error',
          text: `${resError.data.message}`,
          icon: 'error',
        })
        setIsSyncing(false)
      })
      .finally(() => {
        setSyncMessage('Synchronization Complete')
        setTimeout(() => setIsSyncing(false), 500)
      })
  }

  // handle import excel
  const handleImportSubmit = async () => {
    if (!importFile || !detailCountry.rep || !year) return

    const MAX_FILE_SIZE_MB = 5
    const maxFileSize = MAX_FILE_SIZE_MB * 1024 * 1024

    if (importFile.size > maxFileSize) {
      setImportError(`File size exceeds ${MAX_FILE_SIZE_MB}MB. Please upload a smaller file.`)
      return
    }

    setImportLoading(true)
    setImportError(null)

    try {
      const formData = new FormData()
      formData.append('rep', detailCountry.rep)
      formData.append('file', importFile)
      formData.append('year', year)
      formData.append('hs_vr', hsCode)

      importDataManual(axiosPrivate, formData)
        .then((res) => {
          if (res.status === 200) {
            setShowImportModal(false)
            ReactSwal.fire({
              title: 'Import Successful',
              icon: 'success',
              confirmButtonText: 'OK',
            }).then(() => {
              window.location.reload()
            })
          }
        })
        .catch((resErr: any) => {
          const message =
            resErr?.response?.data?.message || 'Upload failed. Please check your file.'
          setImportError(message)
        })
        .finally(() => {
          setImportLoading(false)
        })
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Upload failed. Please check your file.'
      setImportError(message)
    }
  }

  return (
    <>
      {isSyncing && (
        <div className='sync-overlay'>
          <div className='sync-box'>
            <h4>{syncMessage}</h4>
            <p>
              This synchronization process may take a few minutes, during which the system will be
              temporarily unavailable.
            </p>
          </div>
        </div>
      )}
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
          {
            title: 'Trade Data Management',
            path: 'trade-data',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        Detail
      </PageTitle>
      <>
        <div className='TradeDataManagementDetailPage'>
          <Row className='mb-5'>
            <Col>
              <Card>
                <Card.Header className='card-header-stretch'>
                  <Card.Title>
                    <h3 className='text-gray-700'>{detailCountry.name}</h3>
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  {/* <Row className='mb-5'>
                  <Col>
                    <TradeDataDropZone />
                  </Col>
                </Row> */}
                  {showResultSection ? (
                    <>
                      {showNoResultSection && sourceType === 'comtrade' ? (
                        availabilityComtrade ? (
                          <div className='alert-no-result-data'>
                            <div className='alert alert-danger d-flex align-items-center justify-content-between p-5'>
                              <div className='alert-content d-flex align-items-center'>
                                <span className='svg-icon svg-icon-2hx svg-icon-danger me-3'>
                                  <i className='fa-solid fa-triangle-exclamation'></i>
                                </span>
                                <div className='d-flex flex-column'>
                                  <h5 className='mb-1'>Data Not Found</h5>
                                  <span>
                                    Unfortunately, we couldn't find any export data of{' '}
                                    {detailCountry.name} trade flow. Please synchronize it now.
                                  </span>
                                </div>
                              </div>
                              <Button
                                className='ms-2'
                                variant='primary'
                                size='sm'
                                onClick={handleSync}
                              >
                                Synchronize Now
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className='alert-no-result-data'>
                            <div className='alert alert-warning d-flex align-items-center justify-content-between p-5'>
                              <div className='alert-content d-flex align-items-center'>
                                <span className='svg-icon svg-icon-2hx svg-icon-warning me-3'>
                                  <i className='fa-solid fa-triangle-exclamation'></i>
                                </span>
                                <div className='d-flex flex-column'>
                                  <span>
                                    Unfortunately, data of {detailCountry.name} {year} trade flow is
                                    not available at Comtrade. We're showing{' '}
                                    <strong>mirrored data</strong> based on reports from partner
                                    countries stored in our database
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      ) : null}
                      <ListComodity
                        rows={listComodityTableData}
                        counts={listComodityTableData?.length ?? 0}
                        isLoading={isLoadingComodity}
                        sourceType={sourceType}
                        onImportClick={() => setShowImportModal(true)}
                      />
                    </>
                  ) : (
                    ''
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      </>
      <ModalImportData
        show={showImportModal}
        onHide={() => {
          setImportError(null)
          setShowImportModal(false)
          setImportLoading(false)
        }}
        onSubmit={handleImportSubmit}
        onFileChange={(file) => setImportFile(file)}
        isLoading={importLoading}
        error={importError}
      />
    </>
  )
}

export default TradeDataManagementDetailPage
